import { useState, useEffect, useMemo } from "react";

/**
 * The same cascading Category → Product → Watt/Model filter engine
 * already shipped on ShopScreen.jsx, generalized to run over any source
 * product array (e.g. the discounted-only subset on DiscountedProducts.js)
 * instead of duplicating/faking a second filter implementation.
 */
export function useProductFilters(sourceProducts) {
  const [filteredProducts, setFilteredProducts] = useState(sourceProducts);
  const [filterMainCategory, setFilterMainCategory] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterWatt, setFilterWatt] = useState("");

  useEffect(() => {
    setFilteredProducts(sourceProducts);
    setFilterMainCategory("");
    setFilterProduct("");
    setFilterWatt("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceProducts.length]);

  const TREE = useMemo(() => {
    const tree = {};
    sourceProducts.forEach((item) => {
      const main = item.category || "Uncategorized";
      const prod = item.name || "";
      const watts = Array.isArray(item.wattColourModel) ? item.wattColourModel : [];

      if (!tree[main]) tree[main] = {};
      if (!tree[main][prod]) tree[main][prod] = new Set();
      watts.forEach((w) => tree[main][prod].add(w));
    });

    const result = {};
    Object.keys(tree).sort().forEach((main) => {
      result[main] = {};
      Object.keys(tree[main]).sort().forEach((prod) => {
        result[main][prod] = [...tree[main][prod]].sort();
      });
    });
    return result;
  }, [sourceProducts]);

  const mainList = Object.keys(TREE);
  const productsList = filterMainCategory ? Object.keys(TREE[filterMainCategory] || {}) : [];
  const wattList = filterMainCategory && filterProduct ? TREE[filterMainCategory]?.[filterProduct] || [] : [];

  const handleMainChange = (e) => {
    setFilterMainCategory(e.target.value);
    setFilterProduct("");
    setFilterWatt("");
  };

  const handleProductChange = (e) => {
    const value = e.target.value;
    setFilterProduct(value);
    setFilterWatt("");
    const watts = TREE[filterMainCategory]?.[value] || [];
    if (watts.length > 0) setFilterWatt(watts[0]);
  };

  const handleWattChange = (e) => setFilterWatt(e.target.value);

  const applyFilters = () => {
    let temp = [...sourceProducts];

    if (filterMainCategory) {
      temp = temp.filter(
        (p) => (p.category || "Uncategorized").toLowerCase() === filterMainCategory.toLowerCase()
      );
    }
    if (filterProduct) {
      temp = temp.filter((p) => (p.name || "").toLowerCase() === filterProduct.toLowerCase());
    }
    if (filterWatt) {
      temp = temp.filter((p) => {
        if (!p.wattColourModel || !Array.isArray(p.wattColourModel)) return false;
        return p.wattColourModel.some(
          (w) => String(w).trim().toLowerCase() === String(filterWatt).trim().toLowerCase()
        );
      });
    }

    setFilteredProducts(temp);
    return temp;
  };

  const clearFilters = () => {
    setFilterMainCategory("");
    setFilterProduct("");
    setFilterWatt("");
    setFilteredProducts(sourceProducts);
  };

  const activeFilterCount = [filterMainCategory, filterProduct, filterWatt].filter(Boolean).length;

  return {
    filteredProducts,
    filterMainCategory,
    filterProduct,
    filterWatt,
    mainList,
    productsList,
    wattList,
    handleMainChange,
    handleProductChange,
    handleWattChange,
    applyFilters,
    clearFilters,
    activeFilterCount,
  };
}

export default useProductFilters;
